import { type MenuProps, Menu } from 'antd';
import styled, { type DefaultTheme, css } from 'styled-components';

import { Sider } from 'components/ui';
import { BRAND_ENERGY_GRADIENT } from 'utils/color/horizonTheme';
import { FlexContainer } from 'utils/layouts/containers';

const SIDEBAR_TRIGGER_HEIGHT = '48px';

export const Logo = styled(FlexContainer)`
  background: transparent;
  text-align: center;
  overflow: hidden;
  height: 65px;
  justify-content: center;
  border-bottom: 1px solid ${({ theme }) => theme.sidebarBorder};
`;

export const Image = styled.img<{ $collapsed?: boolean; $invertForNavyRail?: boolean }>`
  width: ${({ $collapsed }) => ($collapsed ? '48px' : '100%')};
  max-height: ${({ $collapsed }) => ($collapsed ? '48px' : '52px')};
  object-fit: contain;
  padding: ${({ $collapsed }) => ($collapsed ? '0.5em' : '0.5rem 0.75rem')};
  filter: ${({ $invertForNavyRail }) => ($invertForNavyRail ? 'brightness(0) invert(1)' : 'none')};
`;

// styled-components consumes a `theme` prop, so the dark Menu theme is set inside the wrapped
// component.
export const StyledMenu = styled((props: MenuProps) => <Menu {...props} theme="dark" />)`
  width: 100%;
  .ant-menu-item {
    font-size: 16px !important;
  }
  // antd 5 spaces vertical menu items 4px apart; the nav keeps antd 4's 8px.
  .ant-menu-item:not(:last-child) {
    margin-bottom: 8px;
  }

  // Item links take the item color, not the global link color.
  .ant-menu-title-content > a {
    color: inherit;
  }

  // Icons are brand blue except on the selected and disabled items, which follow their text.
  .ant-menu-item:not(.ant-menu-item-selected):not(.ant-menu-item-disabled) .anticon {
    color: ${({ theme }) => theme.teal};
  }

  // antd's vertical active bar sits on the right; the nav marks the selected row on the left.
  .ant-menu-item-selected {
    box-shadow: inset 3px 0 0 ${({ theme }) => theme.teal};
  }

  .ant-menu-item-disabled {
    display: flex;

    svg {
      stroke: currentColor;
    }

    .ant-menu-title-content {
      display: inline-flex;
      flex-grow: 1;

      span {
        width: 100%;
      }
    }
  }
`;

export const SidebarFooter = styled.div<{ $collapsed?: boolean }>`
  padding: ${({ $collapsed }) => ($collapsed ? '0.75rem 0.5rem 0.25rem' : '0.75rem 1rem 0.25rem')};
  border-top: 1px solid ${({ theme }) => theme.sidebarBorder};
  overflow: hidden;
`;

export const SidebarMeta = styled.div`
  position: absolute;
  right: 0;
  bottom: ${SIDEBAR_TRIGGER_HEIGHT};
  left: 0;
  display: flex;
  flex-direction: column;
`;

export const VersionLabel = styled.span<{ $collapsed?: boolean }>`
  display: block;
  padding: ${({ $collapsed }) => ($collapsed ? '0 0.25rem 0.5rem' : '0 1rem 0.5rem')};
  /* Hide the version so it blends into the navy rail but stays selectable:
   * transparent text is invisible to most users yet readable when highlighted
   * (revealed via ::selection below, with cursor/user-select). */
  ${({ theme }) => css`
    color: transparent;
    ::selection {
      color: ${theme.sidebarText};
      background: ${theme.sidebarBg};
    }
  `}
  font-size: 0.75rem;
  line-height: 1.3;
  text-align: center;
  user-select: text;
  cursor: text;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: clip;
`;

export const PrivacyPolicyLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  white-space: nowrap;
  color: ${({ theme }) => theme.sidebarText};

  &:hover {
    color: ${({ theme }) => theme.teal};
  }
`;

/**
 * antd's Sider component has a 'theme' prop, so we need to rename the
 * styled-components theme prop here.
 *
 * Reskin rail: solid navy (`sidebarBg`) with the SkySpecs energy accent strip
 * along the top edge.
 */
export const StyledSider = styled(Sider)<{ $styledComponentsTheme: DefaultTheme }>`
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${BRAND_ENERGY_GRADIENT};
    z-index: 2;
  }
  border-right: 1px solid ${({ $styledComponentsTheme }) => $styledComponentsTheme.sidebarBorder};
  display: flex;
  flex-direction: column;
  position: relative;

  .ant-layout-sider-children {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 100%;
  }
`;
